import { Box, IconButton } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid-pro";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { Heading3 } from "../../../common/components/styled/styled";
import { EezyCompany } from "../../invoices/invoiceAPI";
import {
  createExpert,
  selectExpertUserById,
  updateExpert,
} from "../../user/expertUsersSlice";
import { ExpertForm } from "./ExpertForm";
import {
  defaultExpertFormValues,
  ExpertFormValues,
  mapExpertToFormValues,
  mapFormToCreateExpertPayload,
  mapFormToUpdateRolesPayload,
} from "./mappers";

interface Props {
  isOpen: boolean;
  eezyCompanyOptions: EezyCompany[];
  expertId: string;
  close: () => void;
}

export const ExpertDialog = ({
  isOpen,
  eezyCompanyOptions,
  expertId,
  close,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const expert = useAppSelector((state) =>
    selectExpertUserById(state, expertId),
  );
  const editExpert = !!expert;

  const defaultValues = defaultExpertFormValues;

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { reset, handleSubmit } = methods;

  const handleCreateExpert = (formData: ExpertFormValues) => {
    dispatch(createExpert(mapFormToCreateExpertPayload(formData))).then(() => {
      reset();
      close();
    });
  };

  const handleUpdateExpert = (formData: ExpertFormValues) => {
    dispatch(
      updateExpert({
        expertId,
        version: expert?.account.version!!,
        authScopes: mapFormToUpdateRolesPayload(formData),
        eezyCompanyIds: formData.eezyCompanies.map(
          (company) => company.eezyCompanyId,
        ),
      }),
    ).then(() => {
      reset();
      close();
    });
  };

  useEffect(() => {
    if (!expert) return;
    reset(mapExpertToFormValues(expert, eezyCompanyOptions, t));
  }, [expert, reset, t, eezyCompanyOptions]);

  return (
    <DialogComponent
      open={isOpen}
      onClose={() => {
        close();
        reset(defaultValues);
      }}
    >
      <Box width="100%">
        <Box
          className={DEFAULT_DRAG_HANDLE_SELECTOR}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ cursor: "grab" }}
        >
          <Heading3>
            {editExpert ? t("settings:editExpert") : t("settings:addExpert")}
          </Heading3>
          <IconButton
            onClick={() => {
              close();
              reset(defaultValues);
            }}
          >
            <GridCloseIcon />
          </IconButton>
        </Box>
        <FormProvider {...methods}>
          <ExpertForm
            onSubmit={
              editExpert
                ? handleSubmit(handleUpdateExpert)
                : handleSubmit(handleCreateExpert)
            }
            eezyCompanyOptions={eezyCompanyOptions}
            expert={expert}
          />
        </FormProvider>
      </Box>
    </DialogComponent>
  );
};
